import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useState } from "react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getDefaultConfig, RainbowKitProvider } from "@rainbow-me/rainbowkit";
import { http, fallback } from "wagmi";
import { WagmiProvider } from "wagmi";
import { mainnet, sepolia } from "wagmi/chains";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./context/AuthContext";
import useAuthStore from "./store/authStore";
import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import { PlatformSettingsProvider, usePlatformSettings } from "./context/PlatformSettingsContext";
import { TopBar, LeftSidebar, BottomNav } from "./components/Layout";
import { MaintenanceNotice } from "./components/MaintenanceNotice";
import ErrorBoundary from "./components/ErrorBoundary";
import DebugBanner from "./components/DebugBanner";
import TrendingModal from "./components/TrendingModal";
import useTrendingTopics from "./hooks/useTrendingTopics";

// Pages
import Login from "./pages/Login";
import CheckEmail from "./pages/CheckEmail";
import AuthCallback from "./pages/AuthCallback";
import Feed from "./pages/Feed";
import Leaderboard from "./pages/Leaderboard";
import LevelInfo from "./pages/LevelInfo";
import Allocation from "./pages/Allocation";
import Offerwall from "./pages/Offerwall";
import Staking from "./pages/Staking";
import Marketplace from "./pages/Marketplace";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import UserProfile from "./pages/UserProfile";
import CommentsPage from "./pages/CommentsPage";
import HashtagPage from "./pages/HashtagPage";
import NotFound from "./pages/NotFound";
import LegalPage from "./pages/LegalPage";

// Captures referral code from URL, saves to localStorage, redirects to /login
function ReferralRedirect() {
  const { referralCode } = useParams();
  if (referralCode) {
    try { localStorage.setItem("chicago_pending_referral", referralCode); } catch {}
  }
  return <Navigate to="/login" replace />;
}

// Admin pages (hidden path — not linked from user-facing UI)
import AdminLogin from "./pages/admin/AdminLogin";
import AdminVerify from "./pages/admin/AdminVerify";
import AdminSetup from "./pages/admin/AdminSetup";
import AdminRegister from "./pages/admin/AdminRegister";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminForgotPassword from "./pages/admin/AdminForgotPassword";
import AdminResetPassword from "./pages/admin/AdminResetPassword";

const ADMIN_SLUG = "/portal-ax92-v1";
let _wagmiConfig = null;
let _queryClient = null;

function getWagmiConfig() {
  if (!_wagmiConfig) {
    const sepoliaRpc = import.meta.env.VITE_SEPOLIA_RPC_URL
    _wagmiConfig = getDefaultConfig({
      appName: "Chicago Web3",
      projectId: import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || "",
      chains: [sepolia, mainnet],
      transports: {
        [sepolia.id]: sepoliaRpc
          ? fallback([http(sepoliaRpc), http()])
          : http(),
        [mainnet.id]: http(),
      },
      ssr: false,
    });
  }
  return _wagmiConfig;
}

function getQueryClient() {
  if (!_queryClient) {
    _queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          refetchOnWindowFocus: false,
          gcTime: 1000 * 60 * 5,
        },
      },
    });
  }
  return _queryClient;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const accessToken = useAuthStore((state) => state.accessToken);
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-[4px] border-on-surface border-t-primary-container animate-spin" />
      </div>
    );
  }

  // Token is set but user context hasn't caught up yet (post-login race).
  if (accessToken && !user) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-[4px] border-on-surface border-t-primary-container animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: pathname }} replace />;
  return children;
}

// Admin route guard — redirects to admin login if not authenticated as admin
function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-[4px] border-on-surface border-t-primary-container animate-spin" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to={ADMIN_SLUG} state={{ from: pathname }} replace />;
  }

  return children;
}

// Feature-flag guard — shows a maintenance notice instead of the page when
// the admin has toggled that feature off in Platform Config, rather than
// rendering the page as if nothing happened.
function FeatureRoute({ flag, children }) {
  const { featureFlags, loading } = usePlatformSettings();
  if (loading) return children; // avoid a flash of the notice while settings are still loading
  if (featureFlags?.[flag] === false) {
    return <MaintenanceNotice />;
  }
  return children;
}

// ── App shell — hides nav on auth / admin / standalone routes ──
const AUTH_PATHS = ["/login", "/check-email", "/register", "/magic-link", "/auth/magic-link/verify", "/terms", "/privacy", "/youth-protection"];
// Full-page routes with their own header/layout — not part of the main app
// shell (no TopBar/LeftSidebar/BottomNav). /allocation also manages its own
// separate auth entirely (email OTP, see allocationAuth.js) and is
// deliberately NOT behind the main app's ProtectedRoute/auth context.
const STANDALONE_PATHS = ["/allocation"];

function AppShell({ children }) {
  const { pathname } = useLocation();
  const { isAdmin } = useAuth();
  const { maintenanceMode } = usePlatformSettings();
  const isAuthOrAdmin =
    pathname.startsWith(ADMIN_SLUG) ||
    pathname.startsWith("/admin/") ||
    AUTH_PATHS.includes(pathname) ||
    pathname.startsWith("/login/") ||
    pathname === "/auth/callback" ||
    STANDALONE_PATHS.includes(pathname);
  const isCommentsPage = pathname === "/comments";
  const isHome = pathname === "/";

  const [showTrending, setShowTrending] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  // Only fetch trending data when the fire button is actually shown (home route).
  const { topics: trendingTopics, loading: trendingLoading } = useTrendingTopics(10, isHome);

  if (isAuthOrAdmin) return <>{children}</>;

  // Global maintenance mode (Platform Config > Maintenance Mode) takes the
  // whole user-facing app offline. Admins still get through so they can
  // turn it back off without going through the hidden admin URL again.
  if (maintenanceMode?.enabled && !isAdmin) {
    return <MaintenanceNotice fullScreen title="Down for Maintenance" message="Chicago is temporarily offline while we make some updates. Please check back shortly." />;
  }

  return (
    <div className="bg-background text-on-background font-body-md overflow-x-hidden selection:bg-primary-container selection:text-on-primary-fixed min-h-screen">
      <TopBar />
      <main
        className={`max-w-container-max mx-auto ${isCommentsPage ? "" : "flex"} px-4 lg:px-6 py-gutter relative min-h-screen pt-24 md:pt-28`}
      >
        {!isCommentsPage && <LeftSidebar />}
        {children}
      </main>
      {!isCommentsPage && <BottomNav />}
      {import.meta.env.DEV && <DebugBanner />}
      {isHome && (
        <div className="lg:hidden fixed bottom-24 right-5 z-50 flex flex-col items-end gap-2">
          {showMobileActions && (
            <div className="flex flex-col items-end gap-2" role="menu" aria-label="Quick actions">
              <button
                onClick={() => setShowTrending(true)}
                title="Trending"
                role="menuitem"
                className="flex items-center gap-2 px-3 h-10 bg-surface border-2 border-on-background text-on-background neo-shadow-sm active:translate-y-px"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider">Trending</span>
                <span className="material-symbols-outlined text-xl text-primary">local_fire_department</span>
              </button>
              <button
                onClick={() => {
                  setShowMobileActions(false)
                  window.dispatchEvent(new CustomEvent('chicago:focus-composer'))
                }}
                title="Create post"
                role="menuitem"
                className="flex items-center gap-2 px-3 h-10 bg-surface border-2 border-on-background text-on-background neo-shadow-sm active:translate-y-px"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider">Create post</span>
                <span className="material-symbols-outlined text-xl text-primary">edit_square</span>
              </button>
            </div>
          )}
          <button
            onClick={() => setShowMobileActions(value => !value)}
            title="Quick actions"
            aria-label="Quick actions"
            aria-expanded={showMobileActions}
            className="w-10 h-10 bg-primary-container text-on-primary-fixed flex items-center justify-center border-2 border-on-background active:scale-90 transition-all"
          >
            <span className="material-symbols-outlined text-xl">{showMobileActions ? 'close' : 'local_fire_department'}</span>
          </button>
        </div>
      )}
      {showTrending && (
        <TrendingModal
          topics={trendingTopics}
          loading={trendingLoading}
          onClose={() => setShowTrending(false)}
        />
      )}
    </div>
  );
}

function AppRoutes() {
  return (
    <AppShell>
      <Routes>
        {/* ── Auth (public) ── */}
        <Route path="/login" element={<Login />} />
        <Route path="/login/:referralCode" element={<ReferralRedirect />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/magic-link/verify" element={<AuthCallback />} />
        <Route path="/magic-link" element={<AuthCallback />} />

        {/* ── Protected user routes ── */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Feed />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <FeatureRoute flag="leaderboard">
                <Leaderboard />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocation"
          element={<Allocation />}
        />
        <Route
          path="/levels"
          element={
            <ProtectedRoute>
              <LevelInfo />
            </ProtectedRoute>
          }
        />
        <Route
          path="/offerwall"
          element={
            <ProtectedRoute>
              <FeatureRoute flag="offerwall">
                <Offerwall />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/staking"
          element={
            <ProtectedRoute>
              <FeatureRoute flag="staking">
                <Staking />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace"
          element={
            <ProtectedRoute>
              <Marketplace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute>
              <EditProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId"
          element={
            <ProtectedRoute>
              <UserProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/comments"
          element={
            <ProtectedRoute>
              <CommentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hashtag/:tag"
          element={
            <ProtectedRoute>
              <HashtagPage />
            </ProtectedRoute>
          }
        />

        {/* ── Admin routes (hidden — own path, not linked in user UI) ── */}
        <Route path={`${ADMIN_SLUG}`} element={<AdminLogin />} />
        <Route path={`${ADMIN_SLUG}/verify`} element={<AdminVerify />} />
        <Route path={`${ADMIN_SLUG}/setup`} element={<AdminSetup />} />
        <Route path={`${ADMIN_SLUG}/register`} element={<AdminRegister />} />
        <Route path={`${ADMIN_SLUG}/forgot-password`} element={<AdminForgotPassword />} />
        <Route path={`${ADMIN_SLUG}/reset-password`} element={<AdminResetPassword />} />
        <Route path="/admin/register" element={<AdminRegister />} />
        <Route
          path={`${ADMIN_SLUG}/dashboard`}
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />

        {/* ── Legal (public) ── */}
        <Route path="/terms"             element={<LegalPage />} />
        <Route path="/privacy"           element={<LegalPage />} />
        <Route path="/youth-protection"  element={<LegalPage />} />

        {/* ── 404 ── */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}

const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "";

export default function App() {
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={googleClientId}>
        <WagmiProvider config={getWagmiConfig()}>
          <QueryClientProvider client={getQueryClient()}>
            <RainbowKitProvider>
              <BrowserRouter>
                <ThemeProvider>
                  <AuthProvider>
                    <ToastProvider>
                      <PlatformSettingsProvider>
                        <AppRoutes />
                      </PlatformSettingsProvider>
                    </ToastProvider>
                  </AuthProvider>
                </ThemeProvider>
              </BrowserRouter>
            </RainbowKitProvider>
          </QueryClientProvider>
        </WagmiProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}